/**
 * Whether a new study run in this workspace is visible to the client's colleagues.
 *
 * `organizations.default_simulation_visibility` has existed since spec 25 / increment 05 and the backend has
 * read it on every simulation create — but nothing could WRITE it. Not the admin API, not the seed, not this
 * panel. The 34 sharing organizations on DEV were set with a hand-run SQL UPDATE, which is not a feature, and
 * "configurable per organization" was true of the schema and false of the product.
 *
 * Two things the copy has to say, because both surprise people:
 *
 *   1. It applies to runs made AFTER the change. Visibility is stamped onto each row at creation, so turning
 *      sharing on shows a client nothing until their next study — and turning it off does not retract what is
 *      already shared. That asymmetry is deliberate: resolving visibility at read time instead would make
 *      this toggle a retroactive data-exposure event on every private run the workspace ever made.
 *   2. Sharing is read-only. A colleague can open a shared study; they cannot rename it, re-run it against
 *      the owner's quota, or publish it with a share link.
 *
 * Confirmed before applying, like domain detach, because it changes who can read a brand's work.
 */
import { useState } from 'react';

import { ConfirmInline } from './ConfirmInline';

export const VISIBILITY_PRIVATE = 'private';
export const VISIBILITY_WORKSPACE = 'workspace';

interface SharingSurfaceProps {
  /**
   * The stored value, or null/undefined when the API sent none — shown as unknown rather than guessed.
   *
   * `undefined` is as real a case as `null`: a panel built against a backend that has this field, talking
   * to one deployed before it, receives neither. Both must read as "not reported", because a toggle
   * displaying OFF for a workspace that is actually sharing is worse than one admitting it does not know.
   */
  visibility: string | null | undefined;
  orgName: string;
  onSet: (visibility: string) => Promise<string | null>;
  busy: boolean;
}

export function SharingSurface({ visibility, orgName, onSet, busy }: SharingSurfaceProps) {
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const known = visibility === VISIBILITY_WORKSPACE || visibility === VISIBILITY_PRIVATE;
  const sharing = visibility === VISIBILITY_WORKSPACE;
  const target = sharing ? VISIBILITY_PRIVATE : VISIBILITY_WORKSPACE;

  return (
    <section className="border-t border-gray-100 px-4 py-4 first:border-t-0">
      <h3 className="text-sm font-semibold text-gray-900">Shared studies</h3>
      <p className="mb-2 mt-0.5 text-xs text-gray-500">
        Whether a study one member runs is visible to their colleagues in this workspace. Read-only:
        a colleague can open a shared study, not rename, re-run or publish it.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            sharing ? 'bg-teal-50 text-teal-900' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {!known
            ? 'not reported'
            : sharing
              ? 'Shared across the workspace'
              : 'Private to each member'}
        </span>
        <button
          type="button"
          onClick={() => setPending(target)}
          disabled={busy}
          className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {sharing ? 'Make private' : 'Share across workspace'}
        </button>
      </div>

      {pending && (
        <div className="mt-2">
          <ConfirmInline
            action={pending === VISIBILITY_WORKSPACE ? 'Share' : 'Make private'}
            busy={busy}
            consequence={
              pending === VISIBILITY_WORKSPACE ? (
                <>
                  Studies run in <b>{orgName}</b> from now on will be readable by every colleague in this
                  workspace. Studies already run stay as they are — visibility is stored on each study when
                  it is created, so this does not expose past work.
                </>
              ) : (
                <>
                  Studies run in <b>{orgName}</b> from now on will be private to whoever runs them. Studies
                  already shared <b>stay shared</b>, for the same reason: each one carries the setting it was
                  created under.
                </>
              )
            }
            onConfirm={async () => {
              const failure = await onSet(pending);
              setError(failure);
              setPending(null);
            }}
            onCancel={() => setPending(null)}
          />
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {error}
        </p>
      )}
    </section>
  );
}
